import { ActivityCategory, OutcomeGoalMetric } from "@sp-crm/core";

export const currentYearMonth = (now: Date): string =>
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

export const ymAddMonths = (ym: string, n: number): string => {
    const [year, month] = ym.split("-").map(part => parseInt(part, 10));
    const total = year * 12 + (month - 1) + n;
    const nextYear = Math.floor(total / 12);
    const nextMonth = (total % 12) + 1;
    return `${nextYear}-${String(nextMonth).padStart(2, "0")}`;
};

export const ymLabel = (ym: string): string => {
    const [year, month] = ym.split("-").map(part => parseInt(part, 10));
    return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
    });
};

export const daysInYearMonth = (date: Date): number =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

export const monthElapsedFraction = (ym: string, now: Date): number => {
    const current = currentYearMonth(now);
    if (ym < current) {
        return 1;
    }
    if (ym > current) {
        return 0;
    }
    return now.getDate() / daysInYearMonth(now);
};

export interface Pace {
    key: "none" | "hit" | "missed" | "ontrack" | "slightly" | "behind";
    label: string;
    text: string;
    dot: string;
}

export const metricPace = (actual: number, target: number, frac: number): Pace => {
    if (!target) {
        return {
            key: "none",
            label: "No goal",
            text: "text-gray-400",
            dot: "bg-gray-300",
        };
    }
    if (actual >= target) {
        return {
            key: "hit",
            label: "Goal met",
            text: "text-emerald-700",
            dot: "bg-emerald-500",
        };
    }
    if (frac >= 1) {
        return {
            key: "missed",
            label: "Below goal",
            text: "text-rose-700",
            dot: "bg-rose-500",
        };
    }
    const expected = target * frac;
    if (actual >= expected) {
        return {
            key: "ontrack",
            label: "On track",
            text: "text-emerald-700",
            dot: "bg-emerald-500",
        };
    }
    if (actual >= expected * 0.7) {
        return {
            key: "slightly",
            label: "Slightly behind",
            text: "text-amber-700",
            dot: "bg-amber-500",
        };
    }
    return { key: "behind", label: "Behind", text: "text-rose-700", dot: "bg-rose-500" };
};

export const activityCategoryBarColor: Record<ActivityCategory, string> = {
    visit: "bg-brand-600",
    call: "bg-sky-600",
    sms: "bg-teal-600",
    email: "bg-violet-600",
    event: "bg-emerald-600",
    other: "bg-slate-600",
};

export const outcomeGoalMetricBarColor: Record<OutcomeGoalMetric, string> = {
    referrals: "bg-amber-600",
    careStarts: "bg-emerald-600",
    movesCompleted: "bg-indigo-600",
};
